import ProtectedRoute from './ProtectedRoute';
import { FileTextOutlined } from '@ant-design/icons';
import React, { lazy } from 'react';
// Icon imports
import factoryGreen from './assets/Dashboard/FactoryIconGreen.svg';
import Maintenance from './assets/sideNav/Maintenance.svg';
import MaintenanceGreen from './assets/sideNav/selected/Maintenance.svg';
import Maps from './assets/sideNav/Maps.svg';
import MapsGreen from './assets/sideNav/selected/Maps.svg';
import factory from './assets/Dashboard/FactoryIcon.svg';
import ExecutiveDashboard from './assets/sideNav/ExecutiveDashboard.svg';
import ExecutiveDashboardGreen from './assets/sideNav/selected/ExecutiveDashboard.svg';
import General from './assets/sideNav/General.svg';
import GeneralGreen from './assets/sideNav/selected/General.svg';
import Charger from './assets/sideNav/Charger.svg';
import ChargerGreen from './assets/sideNav/selected/Charger.svg';
import PowerSVG from './assets/sideNav/Power.svg';
import PowerGreen from './assets/sideNav/selected/Power.svg';
import Fuel from './assets/sideNav/Fuel.svg';
import FuelGreen from './assets/sideNav/selected/Fuel.svg';
import Solar from './assets/sideNav/Solar.svg';
import SolarGreen from './assets/sideNav/selected/Solar.svg';
import StarLink from './assets/sideNav/StarLink.svg';
import StarLinkGreen from './assets/sideNav/selected/StarLink.svg';
import Reports from './assets/sideNav/Reports.svg';
import ReportsGreen from './assets/sideNav/selected/Reports.svg';
import Cameras from './assets/sideNav/Camera.svg';
import CamerasGreen from './assets/sideNav/selected/Camera.svg';
import Docs from './assets/sideNav/Docs.svg';
import DocsGreen from './assets/sideNav/selected/Docs.svg';
import Events from './assets/sideNav/Events.svg';
import EventsGreen from './assets/sideNav/selected/EventsSelected.svg';
import Loki from './assets/sideNav/Log.svg';
import LokiGreen from './assets/sideNav/selected/Log.svg';
import {
  ALARMS,
  CAMERAS,
  CHARGERS,
  DASHBOARD,
  EVENTS,
  FACTORY_DASHBOARD,
  FUEL,
  GENERAL,
  GLOBAL_MAPS,
  GLOBAL_REPORTS,
  MAINTENANCE,
  MAPS,
  POWER_GENERATION,
  POWER_DIAG,
  REMOTE,
  REPORTS,
  SOLAR,
  STARLINK,
  LOGS,
  GLOBAL_LOGS,
  MANUALS,
  EXECUTIVE_DASHBOARD,
  EXECUTIVE_DASHBOARD_LABEL,
  SUPPORT,
  DOCUMENTATION
} from './utils/constants';
import { hasUserPermission, normalizeComponentName } from './utils/permissionUtils';

// Lazy load components for better performance
const Chargers = lazy(() => import('./view/charger/Chargers'));
const IndividualCharger = lazy(() => import('./view/charger/IndividualCharger'));
const MapsComponent = lazy(() => import('./view/maps/Maps'));
const Power = lazy(() => import('./view/power/Power'));
const PowerMQTT = lazy(() => import('./view/power/PowerMQTT'));
const PowerIndividual = lazy(() => import('./view/power/PowerIndividual'));
const PowerIndividualMQTT = lazy(() => import('./view/power/PowerIndividualMQTT'));
const FuelComponent = lazy(() => import('./view/fuel/Fuel'));
const FuelIndividual = lazy(() => import('./view/fuel/FuelIndividual'));
const SolarComponent = lazy(() => import('./view/solar/Solar'));
const ReportsComponent = lazy(() => import('./view/reports/GlobalReports'));
const Camera = lazy(() => import('./view/cameras/Cameras'));
const StarLinkComponent = lazy(() => import('./view/starlink/Starlink'));
const RemoteComponent = lazy(() => import('./view/remote/Remote2'));
const LokiLogging = lazy(() => import('./view/lokiLogging/LokiLogging'));

export type ComponentConfig = {
  component: React.LazyExoticComponent<React.ComponentType<any>>;
  individualComponent?: React.LazyExoticComponent<React.ComponentType<any>>;
  wrapper: React.ComponentType<any>;
  hasIndividual?: boolean;
  paramName?: string;
  icons?: {
    default: string;
    selected: string;
  };
};

export type ComponentRegistryType = {
  [key: string]: ComponentConfig;
};

export type MenuItem = {
  label: string;
  key: string;
  icon: React.ReactNode;
  children?: MenuItem[] | null;
  onClick?: (e?: any) => void;
  disabled?: boolean;
  onTitleClick?: () => void;
};

export type MenuItemsConfig = {
  presentComponents: string[];
  chargerMenuItems: MenuItem[];
  selectedCustomerName: string;
  selectedSite: string;
  selectedKey: string;
  permittedCustomers: any[];
  customerSites: any[];
  userInfo: any;
  FactoryPath: boolean;
  ExecutivePath: boolean;
  loadingPower: boolean;
  onMenuItemClick: (item: string) => void;
  onChargerMenuItemClick: (e: any) => void;
  navigate: (path: string) => void;
  navItems: MenuItem[];
  isNewIndustry: boolean;
};

// Merged Component Registry with icons
export const COMPONENT_REGISTRY: ComponentRegistryType = {
  [CHARGERS]: {
    component: Chargers,
    individualComponent: IndividualCharger,
    wrapper: ProtectedRoute,
    hasIndividual: true,
    paramName: 'chargerId',
    icons: { default: Charger, selected: ChargerGreen }
  },
  [MAPS]: {
    component: MapsComponent,
    wrapper: ProtectedRoute,
    icons: { default: Maps, selected: MapsGreen }
  },
  [POWER_GENERATION]: {
    component: Power,
    individualComponent: PowerIndividual,
    wrapper: ProtectedRoute,
    hasIndividual: true,
    paramName: 'powerId',
    icons: { default: PowerSVG, selected: PowerGreen }
  },
  [POWER_DIAG]: {
    component: PowerMQTT,
    individualComponent: PowerIndividualMQTT,
    wrapper: ProtectedRoute,
    hasIndividual: true,
    paramName: 'powerId',
    icons: { default: PowerSVG, selected: PowerGreen }
  },
  [FUEL]: {
    component: FuelComponent,
    individualComponent: FuelIndividual,
    wrapper: ProtectedRoute,
    hasIndividual: true,
    paramName: 'fuelId',
    icons: { default: Fuel, selected: FuelGreen }
  },
  [SOLAR]: {
    component: SolarComponent,
    wrapper: ProtectedRoute,
    icons: { default: Solar, selected: SolarGreen }
  },
  [REPORTS]: {
    component: ReportsComponent,
    wrapper: ProtectedRoute,
    icons: { default: Reports, selected: ReportsGreen }
  },
  [CAMERAS]: {
    component: Camera,
    wrapper: ProtectedRoute,
    icons: { default: Cameras, selected: CamerasGreen }
  },
  [STARLINK]: {
    component: StarLinkComponent,
    wrapper: ProtectedRoute,
    icons: { default: StarLink, selected: StarLinkGreen }
  },
  [EVENTS]: {
    component: RemoteComponent,
    wrapper: ProtectedRoute,
    icons: { default: Events, selected: EventsGreen },
  },
  [LOGS]: {
    component: LokiLogging,
    wrapper: ProtectedRoute,
    icons: { default: Loki, selected: LokiGreen }
  }
};

// For components that don't have lazy-loaded components but still need icons
export const ADDITIONAL_ICON_MAP: Record<string, { default: string; selected: string }> = {
  [EXECUTIVE_DASHBOARD]: { default: ExecutiveDashboard, selected: ExecutiveDashboardGreen },
  [FACTORY_DASHBOARD]: { default: factory, selected: factoryGreen },
  [GENERAL]: { default: General, selected: GeneralGreen },
  [MAINTENANCE]: { default: Maintenance, selected: MaintenanceGreen },
  [GLOBAL_MAPS]: { default: Maps, selected: MapsGreen },
  [REMOTE]: { default: General, selected: GeneralGreen },
  [GLOBAL_LOGS]: { default: Loki, selected: LokiGreen },
  [MANUALS]: { default: Docs, selected: DocsGreen }
};

// Helper function to get icons for any component
export const getComponentIcons = (componentKey: string) => {
  return COMPONENT_REGISTRY[componentKey]?.icons || ADDITIONAL_ICON_MAP[componentKey];
};

// Helper function to get all icons (merged)
export const getAllIcons = () => {
  const registryIcons: Record<string, { default: string; selected: string }> = {};

  Object.keys(COMPONENT_REGISTRY).forEach(key => {
    if (COMPONENT_REGISTRY[key].icons) {
      registryIcons[key] = COMPONENT_REGISTRY[key].icons!;
    }
  });

  return { ...registryIcons, ...ADDITIONAL_ICON_MAP };
};

// Helper function to get icon for present item
export const getIconForPresentItem = (item: string, selectedKey: string): React.ReactNode => {
  const icons = getComponentIcons(item);
  if (!icons) return null;

  return React.createElement('img', {
    src: selectedKey === item ? icons.selected : icons.default,
    alt: item,
    className: 'colorChangeWhenSelected'
  });
};

// Main menu items generation function
export const generateMenuItems = (config: MenuItemsConfig): MenuItem[] => {
  const {
    presentComponents,
    chargerMenuItems,
    selectedCustomerName,
    selectedKey,
    permittedCustomers,
    customerSites,
    userInfo,
    FactoryPath,
    ExecutivePath,
    loadingPower,
    onMenuItemClick,
    onChargerMenuItemClick,
    navigate,
    navItems,
  } = config;

  let updatedItems = [...navItems];

  // Remove Introduction based on userInfo.show_intro
  if (userInfo && userInfo.show_intro === false) {
    updatedItems = updatedItems.filter((item) => item?.key !== 'Introduction');
  }

  // Remove Dashboard if only one customer site
  if (customerSites.length === 1) {
    updatedItems = updatedItems.filter((item) => item?.key !== DASHBOARD);
  }

  // Handle Factory Path filtering
  if (
    selectedCustomerName?.length > 0 &&
    permittedCustomers?.length > 0 &&
    !FactoryPath && !ExecutivePath
  ) {
    updatedItems = updatedItems.filter(
      (item) => ![GLOBAL_REPORTS, FACTORY_DASHBOARD, EXECUTIVE_DASHBOARD].includes(item?.key)
    );
  }

  if (
    selectedCustomerName?.length > 0 &&
    permittedCustomers?.length > 0 &&
    ExecutivePath
  ) {
    updatedItems.push({
      label: EXECUTIVE_DASHBOARD_LABEL,
      key: EXECUTIVE_DASHBOARD,
      icon: React.createElement('img', {
        alt: 'factory',
        src: selectedKey === EXECUTIVE_DASHBOARD ? ExecutiveDashboardGreen : ExecutiveDashboard,
        className: 'colorChangeWhenSelected'
      }),
      children: null,
      onClick: () => navigate(`${selectedCustomerName}/view/${EXECUTIVE_DASHBOARD}`),
      disabled: false,
      onTitleClick: () => navigate(`${selectedCustomerName}/view/${EXECUTIVE_DASHBOARD}`),
    });
  }

  if (
    selectedCustomerName?.length > 0 &&
    permittedCustomers?.length > 0 &&
    FactoryPath
  ) {
    updatedItems.push({
      label: 'FACTORY VIEW',
      key: FACTORY_DASHBOARD,
      icon: React.createElement('img', {
        alt: 'factory',
        src: selectedKey === FACTORY_DASHBOARD ? factoryGreen : factory,
        className: 'colorChangeWhenSelected'
      }),
      children: null,
      onClick: () => navigate(`${selectedCustomerName}/view/${FACTORY_DASHBOARD}`),
      disabled: false,
      onTitleClick: () => navigate(`${selectedCustomerName}/view/${FACTORY_DASHBOARD}`),
    });
  }

  // Add Global Reports
  if (
    selectedCustomerName?.length > 0 &&
    permittedCustomers?.length > 0 &&
    permittedCustomers.filter(
      (customer) => customer.customer === selectedCustomerName
    )?.[0]?.preferences?.report === true &&
    hasUserPermission(userInfo, REPORTS, selectedCustomerName)
  ) {
    updatedItems.push({
      label: FactoryPath ? 'GLOBAL REPORTS' : 'REPORTS',
      key: GLOBAL_REPORTS,
      icon: React.createElement(FileTextOutlined, {
        style: {
          fontSize: '22px',
          color: selectedKey === GLOBAL_REPORTS ? '#38B34A' : undefined
        }
      }),
      children: null,
      onClick: () => navigate(`${selectedCustomerName}/view/Reports`),
      disabled: false,
      onTitleClick: () => navigate(`${selectedCustomerName}/view/Reports`),
    });
  }

  // Build a set of keys already present from customer-preference navItems (case-insensitive)
  const existingKeysLower = new Set(updatedItems.map((i) => i?.key?.toLowerCase()));

  // Add items based on presentComponents (filtered by user permissions)
  // Skip items already added by customer-level preferences to avoid duplicates
  presentComponents?.forEach((rawItem: string) => {
    const item = normalizeComponentName(rawItem); // normalize case: "chargers" → "Chargers", "fuel" → "Fuel", etc.

    if (existingKeysLower.has(item.toLowerCase())) {
      return; // Already added by customer preferences — skip duplicate
    }

    if (!hasUserPermission(userInfo, item, selectedCustomerName)) {
      return;
    }

    if (item === CHARGERS) {
      updatedItems.push({
        label: 'CHARGERS',
        key: 'Chargers',
        icon: React.createElement('img', {
          src: selectedKey === CHARGERS ? ChargerGreen : Charger,
          alt: 'charger',
          className: 'colorChangeWhenSelected'
        }),
        children: chargerMenuItems,
        onClick: onChargerMenuItemClick,
        disabled: false,
        onTitleClick: () => onMenuItemClick(item),
      });
    }
    else if (item === POWER_GENERATION) {
      updatedItems.push({
        label: POWER_GENERATION.toLocaleUpperCase(),
        key: POWER_GENERATION,
        icon: React.createElement('img', {
          src: selectedKey === POWER_GENERATION ? PowerGreen : PowerSVG,
          alt: 'power',
          className: 'colorChangeWhenSelected'
        }),
        children: null,
        onClick: () => onMenuItemClick(item),
        disabled: loadingPower,
        onTitleClick: () => onMenuItemClick(item),
      });
    }
    else if (item === (POWER_DIAG)) {
      updatedItems.push({
        label: 'POWER DIAG',
        key: POWER_DIAG,
        icon: React.createElement('img', {
          src: selectedKey === POWER_DIAG ? PowerGreen : PowerSVG,
          alt: 'powerdiag',
          className: 'colorChangeWhenSelected'
        }),
        children: null,
        onClick: () => onMenuItemClick(POWER_DIAG),
        disabled: loadingPower,
        onTitleClick: () => onMenuItemClick(POWER_DIAG),
      });
    }
    else if (item === FUEL) {
      updatedItems.push({
        label: FUEL.toLocaleUpperCase(),
        key: FUEL,
        icon: React.createElement('img', {
          src: selectedKey === FUEL ? FuelGreen : Fuel,
          alt: 'fuel',
          className: 'colorChangeWhenSelected'
        }),
        children: null,
        onClick: () => onMenuItemClick(item),
        disabled: loadingPower,
        onTitleClick: () => onMenuItemClick(item),
      });
    } else {
      updatedItems.push({
        label: item.toLocaleUpperCase(),
        key: item,
        icon: getIconForPresentItem(item, selectedKey),
        children: null,
        onClick: () => onMenuItemClick(item),
        disabled: undefined,
        onTitleClick: undefined,
      });
    }
  });

  return updatedItems;
};

// Filter menu items based on selection context
export const filterMenuItems = (
  menuItems: MenuItem[],
  selectedCustomerName: string,
  selectedSite: string
): MenuItem[] => {
  const items = selectedCustomerName && !selectedSite
    ? menuItems.filter((item) =>
      [DASHBOARD, FACTORY_DASHBOARD, EXECUTIVE_DASHBOARD, GLOBAL_MAPS, MAINTENANCE, GLOBAL_REPORTS, REMOTE, GLOBAL_LOGS, MANUALS, DOCUMENTATION, SUPPORT].includes(item.key)
    )
    : selectedSite
      ? menuItems.filter((item) => ![GLOBAL_MAPS, REMOTE, GLOBAL_LOGS, GLOBAL_REPORTS].includes(item.key))
      : menuItems;

  // Reorder items: Executive Dashboard first, Factory Dashboard second, then others except Maintenance/Manuals/Documentation/Support, then Maintenance, Manuals, Documentation, Support last
  return [
    ...items.filter((item) => item?.key === EXECUTIVE_DASHBOARD),
    ...items.filter((item) => item?.key === FACTORY_DASHBOARD),
    ...items.filter((item) => item?.key !== EXECUTIVE_DASHBOARD && item?.key !== FACTORY_DASHBOARD && item?.key !== MAINTENANCE && item?.key !== MANUALS && item?.key !== DOCUMENTATION && item?.key !== SUPPORT),
    ...items.filter((item) => item?.key === MAINTENANCE),
    ...items.filter((item) => item?.key === MANUALS),
    ...items.filter((item) => item?.key === DOCUMENTATION),
    ...items.filter((item) => item?.key === SUPPORT)
  ];
};

// Complete menu generation and filtering function
export const createMenuItems = (config: MenuItemsConfig): MenuItem[] => {
  const menuItems = generateMenuItems(config);
  return filterMenuItems(menuItems, config.selectedCustomerName, config.selectedSite);

};